import { usePromotePageData, usePromotePageState } from 'src/app/pages/promote/pageContext';
import { useMembershipAskModal } from 'src/app/shared/components/membershipAsk';
import type { PaymentType } from 'src/app/shared/utils/payments';
import { paymentMethod } from 'src/app/shared/utils/payments';

import { useEnforceMembershipAmount } from '../useEnforceMembershipAmount';
import { useIsExistingMember } from '../useIsExistingMember';
import { useIsMembershipEligible } from '../useIsMembershipEligible';
import { useSaveBraintreePaymentTemporarily } from '../useSaveBraintreePaymentTemporarily';
import { useUpdateTrackingEventsWithGuestData } from '../useUpdateTrackingEventsWithGuestData';
import { useUserInfo } from '../useUserInfo';

import { useFetchLocalizedPPMUHeadlineData } from './hooks/useFetchLocalizedPPMUHeadlineData';
import { useTokenizationData } from './hooks/useTokenizationData';
import { useTrackingProperties } from './hooks/useTrackingProperties';

type Props = {
	token?: string | null;
	accountId?: string | null;
	paymentType: PaymentType;
};

type Result = { accepted: boolean; amount: number } | undefined;

// eslint-disable-next-line max-lines-per-function
export function usePPMU(): (props: Props) => Promise<Result> {
	const isEligibleForMembership = useIsMembershipEligible();

	const isExistingMember = useIsExistingMember();

	const openModal = useMembershipAskModal();

	const [{ usingSavedPaymentMethod }] = usePromotePageState();
	const {
		data: {
			currency,
			membershipProgram,
			petition: { id: petitionId },
			featureConfigs: { useDonationLanguage },
			revenuePpmuMultiplePricesExperiment,
			revenuePpmuLocalizedHeadlineExperiment,
		},
	} = usePromotePageData();

	const getMembershipAmount = useEnforceMembershipAmount();

	const properties = useTrackingProperties();

	const updatedName = useUpdateTrackingEventsWithGuestData();
	const userInfo = useUserInfo();
	const savingBraintreePaymentTemporarilyEnabled = useSaveBraintreePaymentTemporarily();
	const getTokenizationData = useTokenizationData();
	const ppmuLocalizedHeadlineData = useFetchLocalizedPPMUHeadlineData();

	return async function displayModal({ token, accountId, paymentType }): Promise<Result> {
		if (!isEligibleForMembership) return undefined;

		if (await isExistingMember()) return undefined;

		const { amount, minAmount, maxAmount } = getMembershipAmount();
		const { useUpdatedName } = updatedName(paymentType);

		const modal = openModal({
			amount,
			...userInfo(paymentType),
			currencyCode: currency.code,
			currencyExchangeRate: currency.exchangeRate,
			petitionId,
			useDonationLanguage,
			tokenizationData: getTokenizationData({ paymentType, token, accountId }),
			usingSavedPaymentMethod,
			gateway: paymentMethod(paymentType).gateway,
			membershipProgramId: membershipProgram?.id as string,
			paymentType,
			properties: {
				...properties,
				payment_type: paymentType,
			},
			minAmount,
			maxAmount,
			useUpdatedName,
			savingBraintreePaymentTemporarilyEnabled,
			ppmuLocalizedHeadlineData,
			localizedHeadlineExperimentVariant: revenuePpmuLocalizedHeadlineExperiment.variant,
			revenuePpmuMultiplePricesExpData: revenuePpmuMultiplePricesExperiment,
		});

		return modal.waitForResult();
	};
}
